import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button, Modal } from 'react-bootstrap'
import { AppSelect as Select } from '../../../../../components/Select'
import Swal from 'sweetalert2'

import { KTIcon } from '../../../../../_metronic/helpers'
import { IsGranted, ROLE, useAuth } from '../../../auth'
import { createMerchant, getLinkableUsers, getPlatformLookup, getPsTeamList } from './api'
import {
  CreateMerchantBody,
  CreateMerchantData,
  LinkableUser,
  PlatformLookupOption,
  PsTeamOption,
  WizardRole,
  WizardUserRow,
} from './types'
import { getErrorStatus, getErrorText } from '../../../../common/api/errors'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  onCreated: () => void
}

type Step = 'merchant' | 'platform' | 'users' | 'teams' | 'result'

// 'result' is finalization, not a step — the bar just turns fully green on it
const STEPS: { key: Step; label: string }[] = [
  { key: 'merchant', label: 'Мерчант' },
  { key: 'platform', label: 'Платформа' },
  { key: 'users', label: 'Пользователи' },
  { key: 'teams', label: 'Команды' },
]

const STEP_ORDER: Step[] = ['merchant', 'platform', 'users', 'teams', 'result']

const WizardSteps: React.FC<{ current: Step }> = ({ current }) => {
  const currentPos = STEP_ORDER.indexOf(current)

  return (
    <div className='d-flex gap-3 mb-8'>
      {STEPS.map((s, i) => {
        const done = currentPos > i
        const active = currentPos === i

        return (
          <div key={s.key} className='flex-grow-1'>
            <div
              className={`fs-7 mb-2 ${
                active ? 'fw-bold text-dark' : done ? 'fw-semibold text-success' : 'text-muted'
              }`}
            >
              {s.label}
            </div>
            <div
              className={`border-top border-3 rounded ${
                active ? 'border-primary' : done ? 'border-success' : 'border-gray-300'
              }`}
            />
          </div>
        )
      })}
    </div>
  )
}

const ROLE_LABELS: Record<WizardRole, string> = {
  operator: 'Оператор',
  senior_operator: 'Старший оператор',
  merchant_payments_operator: 'Оператор платежей мерчанта',
  merchant_manager: 'Менеджер мерчанта',
}

const DEFAULT_USERS_COUNT = 2

// Data-visibility teams (ps_team, RLS) preselected by default in the wizard.
const DEFAULT_TEAM_CODES = ['team', 'partners']

// Template default callback URL (mirrors Platform::DEFAULT_CALLBACK_URL on the backend).
// Prefilled on the platform step; when left unchanged it is NOT included in the credentials text.
const DEFAULT_CALLBACK_URL = 'https://example.com/'

const MODE_OPTIONS: { value: WizardUserRow['mode']; label: string }[] = [
  { value: 'new', label: 'Новый' },
  { value: 'existing', label: 'Существующий' },
]

const defaultUsers = (): WizardUserRow[] => [
  { mode: 'new', login: '', role: 'operator' },
  { mode: 'new', login: '', role: 'senior_operator' },
]

const isValidUrl = (value: string): boolean => /^https?:\/\/.+/i.test(value.trim())

const sanitizeName = (value: string): string =>
  value
    .trim()
    .replace(/\s+/g, '_')
    .replace(/[^\p{L}\p{N}_-]/gu, '')

const defaultLoginFor = (role: 'operator' | 'senior_operator', title: string): string => {
  const base = sanitizeName(title)
  if (!base) return ''
  return role === 'operator' ? `operator_${base}_1` : `senior_operator_${base}_1`
}

// One-time credentials rendered as a single copy-friendly block: each field is a label line
// followed by its value indented by 4 spaces; three logical groups separated by a blank line.
const CRED_INDENT = '    '

const credLine = (label: string, value: string): string => `${label}:\n${CRED_INDENT}${value}`

const buildCredentialsText = (data: CreateMerchantData): string => {
  const blocks: string[] = []

  // Данные мерчанта
  const merchant = [
    credLine('Название мерчанта', data.merchant.title),
    credLine('Ключ мерчанта', data.merchant.token),
    credLine('Webhook secret', data.merchant.webhookSecret),
  ]
  blocks.push(merchant.join('\n'))

  // Данные авторизации (платформа)
  const auth = [credLine('Логин платформы', data.platform.login)]
  if (data.platform.password) auth.push(credLine('Пароль платформы', data.platform.password))
  // Skip the callback line entirely when it is the untouched template default.
  if (data.platform.callbackUrl && data.platform.callbackUrl !== DEFAULT_CALLBACK_URL) {
    auth.push(credLine('Callback URL', data.platform.callbackUrl))
  }
  if (data.platform.authToken) {
    auth.push(credLine('Ключ для Bearer авторизации', data.platform.authToken))
  }
  blocks.push(auth.join('\n'))

  // Данные пользователей
  if (data.users.length > 0) {
    const users: string[] = []
    data.users.forEach((u) => {
      users.push(credLine(`${ROLE_LABELS[u.role]} — Логин`, u.login))
      if (u.password) users.push(credLine(`${ROLE_LABELS[u.role]} — Пароль`, u.password))
    })
    blocks.push(users.join('\n'))
  }

  return blocks.join('\n\n')
}

const ModalCreateMerchant: React.FC<Props> = ({ open, onOpenChange, onCreated }) => {
  const { currentUser } = useAuth()
  const isAdmin = IsGranted([ROLE.ROLE_ADMIN], currentUser)

  const [step, setStep] = useState<Step>('merchant')
  const [isSaving, setIsSaving] = useState(false)

  // merchant
  const [title, setTitle] = useState('')
  const [enabled, setEnabled] = useState(true)
  const [allowAnonymousPayments, setAllowAnonymousPayments] = useState(false)
  const [userPageHost, setUserPageHost] = useState('')

  // platform
  const [useExistingPlatform, setUseExistingPlatform] = useState(false)
  const [platformId, setPlatformId] = useState<number | null>(null)
  const [platformLogin, setPlatformLogin] = useState('')
  const [platformCallbackUrl, setPlatformCallbackUrl] = useState(DEFAULT_CALLBACK_URL)
  const [platformOptions, setPlatformOptions] = useState<PlatformLookupOption[]>([])

  // users
  const [users, setUsers] = useState<WizardUserRow[]>(defaultUsers())
  const [linkableUsers, setLinkableUsers] = useState<LinkableUser[]>([])
  // default operator/senior logins auto-fill from the merchant title until manually edited
  const [defaultLoginsEdited, setDefaultLoginsEdited] = useState(false)

  // teams — data-visibility groups (RLS). Always shown; team+partners preselected by default.
  const [teamOptions, setTeamOptions] = useState<PsTeamOption[]>([])
  const [selectedTeams, setSelectedTeams] = useState<string[]>([...DEFAULT_TEAM_CODES])

  const [result, setResult] = useState<CreateMerchantData | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [credsCopied, setCredsCopied] = useState(false)

  // Single copy-friendly credentials block for the finalization screen.
  const credentialsText = useMemo(() => (result ? buildCredentialsText(result) : ''), [result])

  const copyCreds = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(credentialsText)
      setCredsCopied(true)
      setTimeout(() => setCredsCopied(false), 1500)
    } catch {
      /* clipboard unavailable — ignore */
    }
  }, [credentialsText])

  const usersEndRef = useRef<HTMLDivElement>(null)
  const prevUsersLenRef = useRef(users.length)

  const reset = useCallback(() => {
    setStep('merchant')
    setIsSaving(false)
    setError(null)
    setTitle('')
    setEnabled(true)
    setAllowAnonymousPayments(false)
    setUserPageHost('')
    setUseExistingPlatform(false)
    setPlatformId(null)
    setPlatformLogin('')
    setPlatformCallbackUrl(DEFAULT_CALLBACK_URL)
    setCredsCopied(false)
    setUsers(defaultUsers())
    setDefaultLoginsEdited(false)
    setSelectedTeams([...DEFAULT_TEAM_CODES])
    setResult(null)
  }, [])

  // Pre-fill the two default users' logins from the merchant title (operator_<name>_1 / senior_operator_<name>_1)
  // until the manager edits them manually.
  useEffect(() => {
    if (defaultLoginsEdited) return
    setUsers((prev) =>
      prev.map((u, i) =>
        i === 0
          ? { ...u, login: defaultLoginFor('operator', title) }
          : i === 1
          ? { ...u, login: defaultLoginFor('senior_operator', title) }
          : u
      )
    )
  }, [title, defaultLoginsEdited])

  // Scroll the modal body to the bottom when a user row is added.
  useEffect(() => {
    if (step === 'users' && users.length > prevUsersLenRef.current) {
      usersEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
    }
    prevUsersLenRef.current = users.length
  }, [users.length, step])

  useEffect(() => {
    if (!open) {
      reset()
      return
    }
    getPlatformLookup()
      .then((r) => {
        if (r.data.success) setPlatformOptions(r.data.data)
      })
      .catch(() => undefined)
    getLinkableUsers()
      .then((r) => {
        if (r.data.success) setLinkableUsers(r.data.data)
      })
      .catch(() => undefined)
    getPsTeamList()
      .then((r) => {
        if (!r.data.success) return
        const opts = r.data.data
        setTeamOptions(opts)
        // keep only preselected defaults that actually exist in the registry
        setSelectedTeams((prev) => prev.filter((c) => opts.some((t) => t.code === c)))
      })
      .catch(() => undefined)
  }, [open, reset])

  const roleOptions = useMemo(
    () =>
      (Object.keys(ROLE_LABELS) as WizardRole[])
        .filter((role) => role !== 'merchant_manager' || isAdmin)
        .map((role) => ({ value: role, label: ROLE_LABELS[role] })),
    [isAdmin]
  )

  // merchant_manager can only be linked by an admin — hide such users from the picker otherwise
  const linkableOptions = useMemo(
    () => (isAdmin ? linkableUsers : linkableUsers.filter((u) => u.role !== 'merchant_manager')),
    [isAdmin, linkableUsers]
  )

  const merchantValid = title.trim().length > 0 && title.trim().length <= 30
  const platformValid = useExistingPlatform
    ? platformId !== null
    : platformLogin.trim().length > 0 && isValidUrl(platformCallbackUrl)
  const usersValid =
    users.length > 0 &&
    users.every((u) => (u.mode === 'new' ? (u.login ?? '').trim().length > 0 : !!u.userId))
  // Teams are optional (empty ⇒ merchant visible only to management roles).
  const teamsValid = true

  const updateUser = (index: number, patch: Partial<WizardUserRow>) => {
    setUsers((prev) => prev.map((u, i) => (i === index ? { ...u, ...patch } : u)))
  }

  const addUser = () => setUsers((prev) => [...prev, { mode: 'new', login: '', role: 'operator' }])
  const removeUser = (index: number) =>
    setUsers((prev) => (index < DEFAULT_USERS_COUNT ? prev : prev.filter((_, i) => i !== index)))

  const submit = useCallback(async () => {
    const platform: CreateMerchantBody['platform'] = useExistingPlatform
      ? { mode: 'existing', platformId: platformId as number }
      : { mode: 'new', login: platformLogin.trim(), callbackUrl: platformCallbackUrl.trim() }

    const operators = users.map((u) =>
      u.mode === 'new'
        ? { mode: 'new' as const, login: (u.login ?? '').trim(), role: u.role }
        : { mode: 'existing' as const, userId: u.userId as number, role: u.role }
    )

    const body: CreateMerchantBody = {
      title: title.trim(),
      enabled,
      allowAnonymousPayments,
      userPageHost: userPageHost.trim() || null,
      platform,
      operators,
      // send only codes that are actually in the loaded registry — guards against a failed/racing
      // teams-GET leaving unconfirmed defaults that would fail the all-or-nothing backend linking
      psTeamCodes: selectedTeams.filter((c) => teamOptions.some((t) => t.code === c)),
    }

    setError(null)
    setIsSaving(true)
    try {
      const resp = await createMerchant(body)
      if (resp.data.success) {
        setResult(resp.data.data)
        setStep('result')
      } else {
        setError(getErrorText(resp, 'Не удалось создать мерчанта', { withFieldName: false }))
      }
    } catch (e: unknown) {
      // The interceptor rejects with the response itself; this URL is out of its alerts, so this is the only message.
      const status = getErrorStatus(e)
      setError(
        getErrorText(e, status ? `Ошибка сервера (${status})` : 'Ошибка сети, попробуйте снова', {
          withFieldName: false,
        })
      )
    } finally {
      setIsSaving(false)
    }
  }, [
    useExistingPlatform,
    platformId,
    platformLogin,
    platformCallbackUrl,
    users,
    title,
    enabled,
    allowAnonymousPayments,
    userPageHost,
    selectedTeams,
    teamOptions,
  ])

  const close = () => {
    if (!isSaving) onOpenChange(false)
  }

  const finish = () => {
    onOpenChange(false)
    onCreated()
  }

  return (
    <Modal show={open} onHide={close} centered scrollable size='lg' backdrop='static'>
      <Modal.Header closeButton={!isSaving}>
        <Modal.Title>Создать мерчанта</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <WizardSteps current={step} />

        {error && (
          <div className='alert alert-danger d-flex align-items-center'>
            <KTIcon iconName='cross-circle' className='fs-2 text-danger me-3' />
            <span>{error}</span>
          </div>
        )}

        <div style={{ position: 'relative', height: '34vh', overflowY: 'auto' }}>
          {isSaving && (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                background: 'rgba(255,255,255,0.65)',
                zIndex: 5,
              }}
            />
          )}

          {step === 'merchant' && (
            <div className='d-flex flex-column gap-4'>
              <div>
                <label className='form-label required'>Название</label>
                <input
                  className='form-control form-control-solid'
                  maxLength={30}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder='Название мерчанта (до 30 символов)'
                />
              </div>
              <div className='form-check form-switch'>
                <input
                  className='form-check-input'
                  type='checkbox'
                  id='mc-enabled'
                  checked={enabled}
                  onChange={(e) => setEnabled(e.target.checked)}
                />
                <label className='form-check-label' htmlFor='mc-enabled'>
                  Включён
                </label>
              </div>
              <div className='form-check form-switch'>
                <input
                  className='form-check-input'
                  type='checkbox'
                  id='mc-anon'
                  checked={allowAnonymousPayments}
                  onChange={(e) => setAllowAnonymousPayments(e.target.checked)}
                />
                <label className='form-check-label' htmlFor='mc-anon'>
                  Разрешить анонимные платежи
                </label>
              </div>
              <div>
                <label className='form-label'>User page host</label>
                <input
                  className='form-control form-control-solid'
                  value={userPageHost}
                  onChange={(e) => setUserPageHost(e.target.value)}
                  placeholder='необязательно'
                />
              </div>
            </div>
          )}

          {step === 'platform' && (
            <div className='d-flex flex-column gap-4'>
              <div className='form-check form-switch'>
                <input
                  className='form-check-input'
                  type='checkbox'
                  id='mc-existing-platform'
                  checked={useExistingPlatform}
                  onChange={(e) => setUseExistingPlatform(e.target.checked)}
                />
                <label className='form-check-label' htmlFor='mc-existing-platform'>
                  Использовать существующую платформу
                </label>
              </div>

              {useExistingPlatform ? (
                <div>
                  <label className='form-label required'>Платформа</label>
                  <Select
                    options={platformOptions}
                    value={
                      platformId !== null
                        ? platformOptions.find((p) => p.value === platformId) ?? null
                        : null
                    }
                    onChange={(opt: any) => setPlatformId(opt ? opt.value : null)}
                    placeholder='Выберите платформу'
                    isClearable
                    menuPortalTarget={document.body}
                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                  />
                </div>
              ) : (
                <>
                  <div>
                    <label className='form-label required'>Название платформы</label>
                    <input
                      className='form-control form-control-solid'
                      value={platformLogin}
                      onChange={(e) => setPlatformLogin(e.target.value)}
                      placeholder='Название платформы'
                    />
                  </div>
                  <div>
                    <label className='form-label required'>Callback URL</label>
                    <input
                      className='form-control form-control-solid'
                      value={platformCallbackUrl}
                      onChange={(e) => setPlatformCallbackUrl(e.target.value)}
                      placeholder='https://...'
                    />
                  </div>
                </>
              )}
            </div>
          )}

          {step === 'users' && (
            <div className='d-flex flex-column gap-4'>
              {users.map((user, index) => {
                const isDefault = index < DEFAULT_USERS_COUNT

                return (
                  <div key={index} className='border rounded p-4 d-flex flex-column gap-3'>
                    <div className='d-flex justify-content-between align-items-center'>
                      <span className='fw-bold'>
                        {isDefault ? ROLE_LABELS[user.role] : `Пользователь ${index + 1}`}
                      </span>
                      {!isDefault && (
                        <button
                          type='button'
                          className='btn btn-sm btn-light-danger'
                          onClick={() => removeUser(index)}
                          title='Удалить'
                        >
                          <KTIcon iconName='trash' className='fs-5 me-0' />
                        </button>
                      )}
                    </div>

                    {!isDefault && (
                      <div className='d-flex gap-3 flex-wrap'>
                        <div style={{ width: 220 }}>
                          <Select
                            options={MODE_OPTIONS}
                            value={MODE_OPTIONS.find((o) => o.value === user.mode) ?? null}
                            onChange={(opt: any) =>
                              updateUser(index, {
                                mode: (opt ? opt.value : 'new') as WizardUserRow['mode'],
                                login: '',
                                userId: undefined,
                              })
                            }
                            isSearchable={false}
                            menuPortalTarget={document.body}
                            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                          />
                        </div>

                        {user.mode === 'new' && (
                          <div style={{ width: 260 }}>
                            <Select
                              options={roleOptions}
                              value={roleOptions.find((o) => o.value === user.role) ?? null}
                              onChange={(opt: any) =>
                                updateUser(index, {
                                  role: (opt ? opt.value : user.role) as WizardRole,
                                })
                              }
                              isSearchable={false}
                              menuPortalTarget={document.body}
                              styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                            />
                          </div>
                        )}
                      </div>
                    )}

                    {user.mode === 'new' ? (
                      <input
                        className='form-control form-control-solid'
                        value={user.login ?? ''}
                        onChange={(e) => {
                          if (isDefault) setDefaultLoginsEdited(true)
                          updateUser(index, { login: e.target.value })
                        }}
                        placeholder={
                          isDefault
                            ? `Логин (${ROLE_LABELS[user.role]})`
                            : 'Логин нового пользователя'
                        }
                      />
                    ) : (
                      <div className='d-flex flex-column gap-2'>
                        <Select
                          options={linkableOptions}
                          value={linkableOptions.find((o) => o.value === user.userId) ?? null}
                          onChange={(opt: any) =>
                            updateUser(index, {
                              userId: opt ? opt.value : undefined,
                              role: opt ? opt.role : user.role,
                            })
                          }
                          placeholder='Выберите пользователя'
                          isClearable
                          menuPortalTarget={document.body}
                          styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                        />
                        {user.userId != null && (
                          <span className='text-muted fs-7'>Роль: {ROLE_LABELS[user.role]}</span>
                        )}
                      </div>
                    )}
                  </div>
                )
              })}

              <button
                type='button'
                className='btn btn-light-primary align-self-start'
                onClick={addUser}
              >
                <KTIcon iconName='plus' className='fs-5 me-1' />
                Добавить пользователя
              </button>
              <div ref={usersEndRef} />
            </div>
          )}

          {step === 'teams' && (
            <div className='d-flex flex-column gap-4'>
              <div>
                <div className='fw-bold fs-6'>Видимость данных (команды)</div>
                <div className='text-muted fs-7'>
                  Определяет, какие команды видят данные этого мерчанта в системе (RLS на уровне БД,
                  в т.ч. для ИИ-ботов). По умолчанию отмечены Team и Partner merchants.
                </div>
              </div>

              <div className='d-flex flex-column gap-2'>
                {teamOptions.map((t) => {
                  const checked = selectedTeams.includes(t.code)

                  return (
                    <div key={t.code} className='form-check'>
                      <input
                        className='form-check-input'
                        type='checkbox'
                        id={`mc-team-${t.code}`}
                        checked={checked}
                        onChange={(e) =>
                          setSelectedTeams((prev) =>
                            e.target.checked ? [...prev, t.code] : prev.filter((c) => c !== t.code)
                          )
                        }
                      />
                      <label className='form-check-label' htmlFor={`mc-team-${t.code}`}>
                        {t.name} <span className='text-muted fs-8'>({t.code})</span>
                      </label>
                    </div>
                  )
                })}
                {teamOptions.length === 0 && (
                  <span className='text-muted fs-8'>Список команд пуст</span>
                )}
              </div>
            </div>
          )}

          {step === 'result' && result && (
            <div className='d-flex flex-column h-100'>
              <div className='alert alert-warning py-2 px-3 mb-3 fs-7'>
                Пароли и ключи показываются один раз — сохраните их.
              </div>

              <div className='d-flex align-items-center justify-content-between mb-2'>
                <div className='fw-bold fs-6'>Данные мерчанта</div>
                <button
                  type='button'
                  className={`btn btn-sm d-flex align-items-center gap-1 ${
                    credsCopied ? 'btn-success' : 'btn-light-primary'
                  }`}
                  onClick={copyCreds}
                  title='Скопировать'
                >
                  <KTIcon iconName={credsCopied ? 'check' : 'copy'} className='fs-5 me-0' />
                  {credsCopied ? 'Скопировано' : 'Копировать'}
                </button>
              </div>

              <textarea
                className='form-control form-control-solid font-monospace fs-7 flex-grow-1'
                style={{ resize: 'none', minHeight: 0 }}
                readOnly
                value={credentialsText}
                onFocus={(e) => e.currentTarget.select()}
              />
            </div>
          )}
        </div>
      </Modal.Body>

      <Modal.Footer>
        {step === 'merchant' && (
          <>
            <Button variant='secondary' onClick={close} disabled={isSaving}>
              Отмена
            </Button>
            <Button variant='primary' onClick={() => setStep('platform')} disabled={!merchantValid}>
              Далее
            </Button>
          </>
        )}

        {step === 'platform' && (
          <>
            <Button variant='secondary' onClick={() => setStep('merchant')}>
              Назад
            </Button>
            <Button variant='primary' onClick={() => setStep('users')} disabled={!platformValid}>
              Далее
            </Button>
          </>
        )}

        {step === 'users' && (
          <>
            <Button variant='secondary' onClick={() => setStep('platform')}>
              Назад
            </Button>
            <Button variant='primary' onClick={() => setStep('teams')} disabled={!usersValid}>
              Далее
            </Button>
          </>
        )}

        {step === 'teams' && (
          <>
            {!isSaving && (
              <Button variant='secondary' onClick={() => setStep('users')}>
                Назад
              </Button>
            )}
            <Button variant='primary' onClick={submit} disabled={!teamsValid || isSaving}>
              {!isSaving && 'Создать'}
              {isSaving && (
                <span className='indicator-progress' style={{ display: 'block' }}>
                  <span className='spinner-border spinner-border-sm align-middle me-2'></span>
                  Создание...
                </span>
              )}
            </Button>
          </>
        )}

        {step === 'result' && (
          <Button variant='primary' onClick={finish}>
            Готово
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  )
}

export default ModalCreateMerchant
